import React, { useState, useEffect } from "react";
import { Clock, User, AlertCircle, CheckCircle2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import BackButton from "../reusableField/BackButton";
import {
  GET_USER_FORM,
  ADD_UPDATE_USER_RESPONSES_FOR_QUESTIONS,
} from "../services/userApi";
import { useQuery, useMutation } from "@apollo/client";
import "../Home/global.css";

const ResearchSection = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const leadExternalDetails = state?.leadExternalDetails ?? [];
  const [modifiedAt, setModifiedAt] = useState(null);
  const [modifiedBy, setModifiedBy] = useState(null);

  const {
    data: userFormData,
    loading: userFormLoading,
    refetch,
  } = useQuery(GET_USER_FORM, {
    variables: {
      formId: leadExternalDetails[0]?.id,
      formUuid: leadExternalDetails[0]?.formUuid,
    },
  });

  const [
    addUpdateUserResponse,
    { data: userResponseData, loading: userResponseLoading },
  ] = useMutation(ADD_UPDATE_USER_RESPONSES_FOR_QUESTIONS);

  const [formData, setFormData] = useState({});
  const [lastSaved, setLastSaved] = useState(null);
  const [savedBy, setSavedBy] = useState("John Doe");
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const brandRed = "#c23228";

  useEffect(() => {
    console.log("Lead External Details:", leadExternalDetails);
    console.log("User Form Data:", userFormData);
  }, [leadExternalDetails, userFormData]);

  // Initialize form data from API response
  useEffect(() => {
    if (userFormData?.getUserForm?.questions) {
      const initialData = {};
      userFormData.getUserForm.questions.forEach((question) => {
        if (question.userResponse) {
          // For multiple select, parse the JSON string
          if (question.questionType === "MULTIPLESELECTRADIO") {
            try {
              initialData[question.id] = JSON.parse(
                question.userResponse.response
              );
            } catch (e) {
              initialData[question.id] = [];
            }
          } else {
            // For text and radio, use the response directly
            initialData[question.id] = question.userResponse.response;
          }
        } else {
          // No response yet
          if (question.questionType === "MULTIPLESELECTRADIO") {
            initialData[question.id] = [];
          } else {
            initialData[question.id] = "";
          }
        }
      });
      setFormData(initialData);
      setHasUnsavedChanges(false);
    }
  }, [userFormData]);

  useEffect(() => {
    if (userFormData?.getUserForm?.questions) {
      const responsesWithMeta = userFormData.getUserForm.questions
        .map((q) => q.userResponse)
        .filter((r) => r && r.modifiedAt);

      console.log("Responses with metadata:", responsesWithMeta);

      if (responsesWithMeta.length > 0) {
        const latest = responsesWithMeta.reduce((prev, current) =>
          new Date(current.modifiedAt) > new Date(prev.modifiedAt)
            ? current
            : prev
        );

        console.log("Latest response:", latest);
        setModifiedAt(new Date(latest.modifiedAt));
        setModifiedBy(latest.modifiedBy || "Unknown User");
      } else {
        console.log("No responses with metadata found");
        setModifiedAt(null);
        setModifiedBy(null);
      }
    }
  }, [userFormData]);

  const getActiveDependentQuestionIds = (
    questionsList,
    currentFormData,
    parentQuestionId = null
  ) => {
    const activeIds = new Set();

    const questionsToCheck =
      parentQuestionId === null
        ? questionsList
        : questionsList.filter((q) => q.id === parentQuestionId);

    questionsToCheck.forEach((question) => {
      if (question.questionType === "RADIO") {
        const selectedValue = currentFormData[question.id];
        const selectedOption = question.options?.find(
          (opt) => opt.option === selectedValue
        );

        if (selectedOption?.dependentQuestionId) {
          activeIds.add(selectedOption.dependentQuestionId);
        }
      }

      if (question.questionType === "MULTIPLESELECTRADIO") {
        const selectedValues = currentFormData[question.id] || [];

        question.options?.forEach((opt) => {
          if (
            opt.dependentQuestionId &&
            selectedValues.includes(opt.option)
          ) {
            activeIds.add(opt.dependentQuestionId);
          }
        });
      }
    });

    return activeIds;
  };

  const handleSubmit = async () => {
    try {
      const formUuid = userFormData?.getUserForm?.formUuid;
      const today = new Date().toISOString().split("T")[0];

      const questions = userFormData?.getUserForm?.questions || [];
      const activeDependentIds = getActiveDependentQuestionIds(
        questions,
        formData
      );
      const visibleQuestionIds = new Set(
        questions
          .filter((q) => !q.isDependent || activeDependentIds.has(q.id))
          .map((q) => q.id)
      );

      const responses = Object.entries(formData)
        .filter(([questionId]) => visibleQuestionIds.has(parseInt(questionId)))
        .map(([questionId, value]) => {
          const question = questions.find((q) => q.id === parseInt(questionId));

          let responseValue = value;
          if (question?.questionType === "MULTIPLESELECTRADIO") {
            responseValue = JSON.stringify(value);
          }

          return {
            questionId: parseInt(questionId),
            formUuid: formUuid,
            response: responseValue,
            formDate: today,
          };
        });

      const result = await addUpdateUserResponse({
        variables: {
          responses: responses,
          assignToUser: false,
        },
      });

      const mutationResult = result.data?.addUpdateUserResponsesForQuestions;

      if (mutationResult?.success) {
        const updatedResponse = mutationResult.data;

        if (updatedResponse?.modifiedAt) {
          setModifiedAt(new Date(updatedResponse.modifiedAt));
        }

        if (updatedResponse?.modifiedBy) {
          setModifiedBy(updatedResponse.modifiedBy);
        }

        setLastSaved(new Date());
        setShowSaveSuccess(true);
        setHasUnsavedChanges(false);
        setTimeout(() => setShowSaveSuccess(false), 3000);

        // Optional: refetch to ensure UI is fresh
        await refetch();
      }
    } catch (error) {
      console.error("Error saving responses:", error);
      alert("Failed to save responses. Please try again.");
    }
  };

  const handleInputChange = (questionId, value) => {
    const questions = userFormData?.getUserForm?.questions || [];
    const currentQuestion = questions.find((q) => q.id === questionId);
    const dependentQuestionIds =
      currentQuestion?.options
        ?.map((opt) => opt.dependentQuestionId)
        .filter(Boolean) || [];

    setFormData((prev) => {
      const updatedFormData = {
        ...prev,
        [questionId]: value,
      };

      if (dependentQuestionIds.length > 0) {
        const activeDependentIds = getActiveDependentQuestionIds(
          questions,
          updatedFormData,
          questionId
        );

        dependentQuestionIds.forEach((dependentId) => {
          if (!activeDependentIds.has(dependentId)) {
            const dependentQuestion = questions.find((q) => q.id === dependentId);
            updatedFormData[dependentId] =
              dependentQuestion?.questionType === "MULTIPLESELECTRADIO"
                ? []
                : "";
          }
        });
      }

      return updatedFormData;
    });
    setHasUnsavedChanges(true);
  };

  const handleCheckboxChange = (questionId, optionValue) => {
    const questions = userFormData?.getUserForm?.questions || [];
    const currentQuestion = questions.find((q) => q.id === questionId);
    const dependentQuestionIds =
      currentQuestion?.options
        ?.map((opt) => opt.dependentQuestionId)
        .filter(Boolean) || [];

    setFormData((prev) => {
      const currentValues = prev[questionId] || [];
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter((v) => v !== optionValue)
        : [...currentValues, optionValue];

      const updatedFormData = {
        ...prev,
        [questionId]: newValues,
      };

      if (dependentQuestionIds.length > 0) {
        const activeDependentIds = getActiveDependentQuestionIds(
          questions,
          updatedFormData,
          questionId
        );

        dependentQuestionIds.forEach((dependentId) => {
          if (!activeDependentIds.has(dependentId)) {
            const dependentQuestion = questions.find((q) => q.id === dependentId);
            updatedFormData[dependentId] =
              dependentQuestion?.questionType === "MULTIPLESELECTRADIO"
                ? []
                : "";
          }
        });
      }

      return updatedFormData;
    });
    setHasUnsavedChanges(true);
  };

  const formatTimestamp = (dateInput) => {
    if (!dateInput) return "Never";

    let date;
    if (typeof dateInput === "string") {
      date = new Date(dateInput);
    } else {
      date = dateInput;
    }

    if (isNaN(date.getTime())) return "Invalid date";

    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderQuestion = (question, index) => {
    const {
      id,
      question: questionText,
      questionType,
      options,
      maxLines,
      isMandatoryField,
      hintText,
    } = question;

    // Render text field
    if (questionType === "TEXTFORMFIELD") {
      return (
        <div
          key={id}
          className="bg-white rounded-lg shadow-sm border border-slate-200 p-4"
        >
          <label className="block">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="text-sm font-semibold text-slate-900">
                {index + 1}. {questionText}
              </span>
              {isMandatoryField && <span className="text-red-600">*</span>}
            </div>
            <textarea
              value={formData[id] || ""}
              onChange={(e) => handleInputChange(id, e.target.value)}
              placeholder={hintText || `Enter your response...`}
              className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none transition-all resize-none text-sm"
              onFocus={(e) => (e.target.style.borderColor = brandRed)}
              onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
              rows={maxLines || 3}
            />
          </label>
        </div>
      );
    }

    if (questionType === "RADIO") {
      return (
        <div
          key={id}
          className="bg-white rounded-lg shadow-sm border border-slate-200 p-4"
        >
          <label className="block mb-3">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="text-sm font-semibold text-slate-900">
                {index + 1}. {questionText}
              </span>
              {isMandatoryField && <span className="text-red-600">*</span>}
            </div>
            <div
              className={`grid gap-2 ${options.length <= 3
                  ? "grid-cols-3"
                  : options.length <= 5
                    ? "grid-cols-5"
                    : "grid-cols-4"
                }`}
            >
              {options.map((option) => {
                const isSelected = formData[id] === option.option;

                let colorStyle = {};
                colorStyle = isSelected
                  ? {
                    borderColor: brandRed,
                    backgroundColor: "#fef2f2",
                    color: brandRed,
                  }
                  : {};
                // if (questionText.toLowerCase().includes("urgent")) {
                //   if (option.option === "Immediate") {
                //     colorStyle = isSelected
                //       ? {
                //           borderColor: "#dc2626",
                //           backgroundColor: "#fef2f2",
                //           color: "#dc2626",
                //         }
                //       : {};
                //   } else if (option.option === "Soon") {
                //     colorStyle = isSelected
                //       ? {
                //           borderColor: "#d97706",
                //           backgroundColor: "#fffbeb",
                //           color: "#d97706",
                //         }
                //       : {};
                //   } else if (option.option === "Later") {
                //     colorStyle = isSelected
                //       ? {
                //           borderColor: "#64748b",
                //           backgroundColor: "#f8fafc",
                //           color: "#64748b",
                //         }
                //       : {};
                //   }
                // } else {

                // }

                return (
                  <button
                    key={option.id}
                    onClick={() => handleInputChange(id, option.option)}
                    className={`py-1.5 px-3 rounded-md border transition-all text-sm font-medium ${isSelected
                        ? ""
                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                      }`}
                    style={colorStyle}
                  >
                    {option.option}
                  </button>
                );
              })}
            </div>
          </label>
        </div>
      );
    }

    // Render checkboxes (multiple select)
    if (questionType === "MULTIPLESELECTRADIO") {
      return (
        <div
          key={id}
          className="bg-white rounded-lg shadow-sm border border-slate-200 p-4"
        >
          <label className="block">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="text-sm font-semibold text-slate-900">
                {index + 1}. {questionText}
              </span>
              <span className="text-xs text-slate-500 font-normal">
                (Multiple choices)
              </span>
              {isMandatoryField && <span className="text-red-600">*</span>}
            </div>
            <div
              className={`grid gap-2 ${options.length <= 4
                  ? "grid-cols-2 md:grid-cols-4"
                  : "grid-cols-2 md:grid-cols-3"
                }`}
            >
              {options.map((option) => {
                const isChecked = (formData[id] || []).includes(option.option);

                return (
                  <label
                    key={option.id}
                    className={`flex items-center gap-2 p-2.5 rounded-md border cursor-pointer transition-all ${isChecked
                        ? "bg-red-50"
                        : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    style={
                      isChecked
                        ? {
                          borderColor: brandRed,
                        }
                        : {}
                    }
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleCheckboxChange(id, option.option)}
                      className="w-4 h-4 border-slate-300 rounded"
                      style={{ accentColor: brandRed }}
                    />
                    <span className="text-sm font-medium text-slate-900">
                      {option.option}
                    </span>
                  </label>
                );
              })}
            </div>
          </label>
        </div>
      );
    }

    return null;
  };

  if (userFormLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 flex items-center justify-center">
        <div className="flex items-center gap-2 text-slate-600">
          <div className="w-5 h-5 border-2 border-slate-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm">Loading form...</span>
        </div>
      </div>
    );
  }

  const questions = userFormData?.getUserForm?.questions || [];
  const formName = userFormData?.getUserForm?.name || "Presales Research";
  const activeDependentIds = getActiveDependentQuestionIds(questions, formData);
  const visibleQuestions = questions.filter(
    (question) => !question.isDependent || activeDependentIds.has(question.id)
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 global mt-[80px] ml-[80px]">
      <div className="w-full">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <BackButton label="Back" />
              <h1 className="text-lg font-bold text-slate-900 mb-1">
                {formName}
              </h1>
              {/* <p className="text-sm text-slate-600">Structured intelligence to understand and engage your lead effectively</p> */}
            </div>
            <div className="flex items-center gap-2">
              {showSaveSuccess && (
                <div
                  className="flex items-center gap-1.5"
                  style={{ color: brandRed }}
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span className="text-xs">Saved successfully</span>
                </div>
              )}
            </div>
          </div>

          {/* Last updated info */}
          <div className="mt-3 pt-3 border-t border-slate-200 flex items-center gap-3 text-xs text-slate-600">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              <span>Last updated: {formatTimestamp(modifiedAt)}</span>
            </div>
            {modifiedBy && (
              <div className="flex items-center gap-1.5">
                <User className="w-3.5 h-3.5" />
                <span>By: {modifiedBy}</span>
              </div>
            )}
          </div>
          {/* )} */}
        </div>

        {/* Form Content */}
        <div className="space-y-4">
          {questions.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 text-center">
              <p className="text-slate-600">
                No questions available for this form.
              </p>
            </div>
          ) : (
            <>
              {visibleQuestions.map((question, index) =>
                renderQuestion(question, index)
              )}

              {/* Submit Button */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {hasUnsavedChanges && (
                    <div className="flex items-center gap-1.5 text-amber-600">
                      <AlertCircle className="w-3.5 h-3.5" />
                      <span className="text-xs">You have unsaved changes</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={handleSubmit}
                  disabled={userResponseLoading || !hasUnsavedChanges}
                  className="px-6 py-2 rounded-md text-sm font-medium text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: brandRed,
                    opacity:
                      userResponseLoading || !hasUnsavedChanges ? 0.5 : 1,
                  }}
                >
                  {userResponseLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Saving...</span>
                    </div>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </>
          )}

          {/* Info Banner */}
          {/* {questions.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-900">
                <p className="font-medium mb-0.5">Manual save required</p>
                <p className="text-blue-700">Click the Submit button to save your changes. Fields marked with * are mandatory.</p>
              </div>
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default ResearchSection;
